import { uiScale, type ButtonRect, type LayoutRects } from "./layout";

export const tutorialCaption = (round: number): string =>
  [
    "",
    "Swipe left & right to scoop snow",
    "Draw circles to roll a snowball",
    "Swipe up to throw!",
  ][round] ?? "";
// v2r24 (GD ruling c): compact phones (360/375 wide) use short captions; if
// these do not fit between dodge/block, that device falls back to verbs only.
export const COMPACT_CAPTIONS = [
  "",
  "Swipe to scoop!",
  "Circle to roll!",
  "Swipe up!",
];
export const CAPTION_SETS = (): string[][] => [
  [0, 1, 2, 3].map(tutorialCaption),
  COMPACT_CAPTIONS,
  COMPACT_FALLBACK,
];
export const COMPACT_FALLBACK = ["", "Swipe!", "Circle!", "Swipe up!"];
export const matchRound = (phase: string): number =>
  ({ r1_gather: 1, r2_roll: 2, r3_throw: 3, sudden_death: 3 })[phase] ?? 0;
export const snowballCount = (
  round: number,
  snow: number,
  ammo: number,
): number => (round === 1 ? Math.floor(snow) : ammo);
export const lapFraction = (radians: number): number =>
  (Math.max(0, radians) % (2 * Math.PI)) / (2 * Math.PI);

// v3 locked layout (390x844, 34 px home-bar inset): zone y592-650, hint pill
// y662-694, 76 px action circles centred y744. Everything is anchored to the
// controls so it follows each device's bottom inset. Landscape keeps the
// zone on the right.
export function matchHudGeometry(
  width: number,
  height: number,
  controlsTop: number,
  /** Lowest y the SNOW label may reach (the tutorial coach card's bottom). */
  topLimit = 0,
  /** Bottom of the pause / mute row; the SNOW label stays clear below it. */
  headerBottom = 0,
) {
  const s = uiScale(width, height);
  const landscape = width > height;
  const captionHeight = 32 * s;
  const captionY = landscape
    ? height - captionHeight - 4
    : controlsTop - 44 * s;
  const zoneHeight = landscape ? 96 : 58 * s;
  const zoneY = landscape ? captionY - 12 - zoneHeight : controlsTop - 114 * s;
  const zoneWidth = 304 * s;
  const zoneX = landscape ? width - 32 - zoneWidth : (width - zoneWidth) / 2;
  const controlCentre = controlsTop + 38 * s;
  const ammoWidth = 164 * s;
  return {
    scale: s,
    compact: width < height && height < 700,
    zone: { x: zoneX, y: zoneY, width: zoneWidth, height: zoneHeight },
    // The R3 "xN" pill, centred between the action circles.
    counter: {
      x: landscape ? 134 : (width - ammoWidth) / 2,
      y: controlCentre - 22 * s,
      width: ammoWidth,
      height: 44 * s,
    },
    caption: {
      x: zoneX,
      y: captionY,
      width: zoneWidth,
      height: captionHeight,
    },
    // R1/R2 SNOW jar on the right edge (design 01-03): 260 tall on 390x844,
    // y290-550, with the SNOW pill above and the value pill below it.
    jar: (() => {
      const top = Math.max(
        106,
        topLimit + 40 * s,
        landscape ? 0 : headerBottom + 40 * s,
      );
      const room = zoneY - 40 * s - top;
      const h = Math.max(topLimit ? 60 : 100, Math.min(260 * s, room));
      return {
        x: width - 60 * s,
        y: zoneY - 40 * s - h,
        width: 36 * s,
        height: h,
      };
    })(),
    // R2 ball tray, in the row the action circles use in R3.
    tray: {
      x: landscape ? 134 : Math.max(12, (width - 300 * s) / 2),
      y: controlCentre - 22 * s + 8 * s,
      width: Math.min(300 * s, landscape ? zoneX - 12 - 134 : width - 24),
      height: 44 * s,
    },
  };
}

export const HINTS = {
  r1: "Swipe to scoop snow!",
  r2: "Circle to pack a ball!",
  notEnough: "Not enough snow for a ball.",
  r3: "Swipe up to throw!",
  incoming: "Coral ball coming. Block or dodge!",
  empty: "Out of snowballs! Block and dodge.",
} as const;
export const TUTORIAL_COPY = {
  skip: "Skip",
  noTimer: "No timer. Take your time!",
  steps: [
    {
      title: "Scoop the snow",
      lines: ["Swipe left and right in the box.", "Fill the SNOW jar to 40."],
    },
    {
      title: "Pack snowballs",
      lines: ["Draw circles. Each circle uses", "10 snow to pack 1 ball."],
    },
    {
      title: "Throw and defend",
      lines: [
        "Swipe up to throw. When Mika",
        "throws, tap BLOCK or DODGE.",
        "Block or dodge in time for 0 damage.",
      ],
    },
  ],
} as const;
export const tutorialLabel = (step: number): string => `TUTORIAL ${step}/3`;

/** Coach card under the header: 324 wide, 116 tall (138 with three lines). */
export function coachGeometry(
  width: number,
  height: number,
  headerBottom: number,
  step: 1 | 2 | 3,
) {
  const s = uiScale(width, height);
  const lines = TUTORIAL_COPY.steps[step - 1]!.lines.length;
  const w = Math.min(324 * s, width - 32);
  return {
    scale: s,
    card: {
      x: (width - w) / 2,
      y: headerBottom + 21 * s,
      width: w,
      height: (72 + 22 * lines) * s,
    },
  };
}

export const PERFECT_COPY = "PERFECT PACK!";
export const SNOW_LABEL = "SNOW";
export const AMMO_LABEL = "snowballs left";

/** Snow jar fill, 0..1 of the 120-snow cap. */
export const jarFill = (snow: number, cap = 120): number =>
  Math.max(0, Math.min(1, snow / cap));
/** Balls the snow on hand can still pack (10 snow each). */
export const ballsFromSnow = (snow: number): number => Math.floor(snow / 10);
/** R2 tray: filled slots = balls packed, total = packed + still packable. */
export const trayModel = (snow: number, ammo: number) => ({
  filled: ammo,
  total: ammo + ballsFromSnow(snow),
});
/** R3 hint: incoming warning wins, then the empty state, then the default. */
export const r3Hint = (ammo: number, incoming: boolean): string =>
  incoming ? HINTS.incoming : ammo <= 0 ? HINTS.empty : HINTS.r3;

export class TutorialState {
  private completed = new Set<number>();
  seen: boolean;
  constructor(
    private storage?: Pick<Storage, "getItem" | "setItem">,
    override?: string | null,
  ) {
    let seen = false;
    try {
      seen = storage?.getItem("tutorialSeen") === "true";
    } catch {
      /* Storage can be disabled. */
    }
    this.seen =
      override === "fresh" ? false : override === "seen" ? true : seen;
  }
  visible(round: number): boolean {
    return round > 0 && !this.seen && !this.completed.has(round);
  }
  success(round: number): void {
    this.completed.add(round);
  }
  finish(): void {
    this.seen = true;
    try {
      this.storage?.setItem("tutorialSeen", "true");
    } catch {
      /* Best effort. */
    }
  }
}

// The R3 pill sits in the gap between the two action circles.
export function matchAmmoRect(layout: LayoutRects): ButtonRect {
  const [left, right] =
    layout.dodgeArt.x < layout.blockArt.x
      ? ([layout.dodgeArt, layout.blockArt] as const)
      : ([layout.blockArt, layout.dodgeArt] as const);
  const gap = right.x - (left.x + left.width);
  const width = Math.min(layout.ammo.width, gap - 12);
  return {
    ...layout.ammo,
    x: (left.x + left.width + right.x - width) / 2,
    width,
  };
}

/** Phases that draw a DOM overlay instead of the gesture HUD. */
export type OverlayKind = "countdown" | "handoff" | "banner3" | "banner1";
export const overlayKind = (phase: string): OverlayKind | null =>
  (
    ({
      countdown: "countdown",
      banner_r2: "handoff",
      banner_r3: "banner3",
      banner_r1: "banner1",
    }) as Record<string, OverlayKind>
  )[phase] ?? null;

/** Tick of the 120-tick R1 banner at which the GO! disc appears. */
export const GO_SHOWN_FROM_TICK = 80;

export const COUNTDOWN_COPY = {
  pill: "Get ready to scoop!",
  roundDone: "ROUND 1 DONE",
  title: "Nice scooping!",
  snow: "SNOW",
  upTo: "UP TO",
  rule: "10 snow packs 1 snowball",
  next: "Next: circle to pack!",
  /** Handoff with fewer than 10 snow (UP TO x0): replaces `next`. */
  notEnough: "Not enough snow. Try again!",
} as const;

/**
 * The digit shown at `phaseTick` (60 Hz) and the fraction of that digit's
 * second still left. The opening countdown is 3-2-1; the 2 s round banners
 * merge 2-1-GO! into thirds so their length does not change.
 */
export function countdownStep(
  phase: string,
  phaseTick: number,
): { label: "3" | "2" | "1" | "GO!"; remaining: number } | null {
  const t = Math.max(0, phaseTick);
  if (phase === "countdown") {
    const i = Math.min(2, Math.floor(t / 60));
    return {
      label: (["3", "2", "1"] as const)[i]!,
      remaining: 1 - Math.min(1, (t - i * 60) / 60),
    };
  }
  // The "ROUND 1: GATHER!" banner shows the GO! disc for its last 40 ticks.
  if (phase === "banner_r1") {
    if (t < GO_SHOWN_FROM_TICK) return null;
    return {
      label: "GO!",
      remaining: 1 - Math.min(1, (t - GO_SHOWN_FROM_TICK) / 40),
    };
  }
  if (phase === "banner_r2" || phase === "banner_r3") {
    const i = Math.min(2, Math.floor(t / 40));
    return {
      label: (["2", "1", "GO!"] as const)[i]!,
      remaining: 1 - Math.min(1, (t - i * 40) / 40),
    };
  }
  return null;
}
